import React, { useState } from 'react';
import styled from 'styled-components';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaPaperPlane, FaCheckCircle, FaClock, FaArrowRight } from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 180px;
  min-height: calc(100vh - 140px);
  background: #ffffff;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-top: 155px;
  }


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

/* ========== TOP SECTION ========== */
const TopSection = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2.5rem 5% 1.5rem;
  text-align: center;

  @media (max-width: 768px) {
    padding: 2rem 4% 1rem;
  }
`;

const TopTitle = styled.h1`
  font-family: 'Times New Roman', serif;
  font-size: 2.4rem;
  color: #1a1a2e;
  font-weight: 800;
  margin-bottom: 0.5rem;

  span { color: #3730A3; }

  @media (max-width: 768px) { font-size: 1.8rem; }
  
  @media (max-width: 1366px) and (min-width: 769px) { font-size: 2rem; }
`;

const TopSubtitle = styled.p`
  font-family: 'Times New Roman', serif;
  color: #4a4a6a;
  font-size: 1rem;
  max-width: 500px;
  margin: 0 auto;
`;

/* ========== MAIN LAYOUT ========== */
const MainLayout = styled.div`
  max-width: 1000px;
  margin: 0 auto 2rem;
  padding: 0 5%;
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* ========== IMAGE PANEL ========== */
const ImagePanel = styled.div`
  background: linear-gradient(135deg, #3730A3 0%, #0b0b77 100%);
  border-radius: 20px 0 0 20px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -30px;
    right: -30px;
    width: 120px;
    height: 120px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }

  @media (max-width: 900px) {
    border-radius: 20px 20px 0 0;
    padding: 1.5rem;
    min-height: auto;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1.5rem;
  }
`;

const ImageContent = styled.div`
  position: relative;
  z-index: 2;
  color: white;
`;

const ImageTitle = styled.h2`
  font-family: 'Times New Roman', serif;
  font-size: 1.8rem;
  font-weight: 800;
  margin-bottom: 0.8rem;
  color: white;

  span { color: #ffd966; }

  @media (max-width: 1366px) and (min-width: 769px) { font-size: 1.5rem; }
  @media (max-width: 768px) { font-size: 1.4rem; }
`;

const ImageText = styled.p`
  font-family: 'Times New Roman', serif;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: rgba(255, 255, 255, 0.9);

  .icon-circle {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    flex-shrink: 0;
  }

  .info {
    font-size: 0.85rem;
    line-height: 1.4;
  }
`;

const ImageBottom = styled.div`
  position: relative;
  z-index: 2;
  margin-top: 1.5rem;

  img {
    width: 100%;
    max-width: 350px;
    height: auto;
    border-radius: 14px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);

    @media (max-width: 1366px) and (min-width: 769px) { max-width: 380px; }
    @media (max-width: 900px) { max-width: 250px; }
  }
`;

/* ========== FORM PANEL ========== */
const FormPanel = styled.div`
  background: white;
  border-radius: 0 20px 20px 0;
  padding: 2rem;
  box-shadow: 0 8px 30px rgba(55, 48, 163, 0.05);
  border: 1px solid #f0f0f5;
  border-left: none;

  @media (max-width: 900px) {
    border-radius: 0 0 20px 20px;
    border-left: 1px solid #f0f0f5;
    border-top: none;
    padding: 1.5rem;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1.5rem;
  }
`;

const FormTitle = styled.h2`
  font-family: 'Times New Roman', serif;
  font-size: 1.3rem;
  color: #1a1a2e;
  font-weight: 700;
  margin-bottom: 0.2rem;
`;

const FormSubtitle = styled.p`
  font-family: 'Times New Roman', serif;
  color: #4a4a6a;
  font-size: 0.85rem;
  margin-bottom: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem;

  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const Label = styled.label`
  font-family: 'Times New Roman', serif;
  font-size: 0.78rem;
  font-weight: 600;
  color: #1a1a2e;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  span { color: #e42932; }
`;

const Input = styled.input`
  font-family: 'Times New Roman', serif;
  padding: 0.65rem 0;
  border: none;
  border-bottom: 2px solid #e8e8ec;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: transparent;

  &:focus { outline: none; border-bottom-color: #3730A3; }
`;

const TextArea = styled.textarea`
  font-family: 'Times New Roman', serif;
  padding: 0.65rem 0;
  border: none;
  border-bottom: 2px solid #e8e8ec;
  font-size: 0.9rem;
  min-height: 80px;
  resize: vertical;
  transition: all 0.3s ease;
  background: transparent;

  &:focus { outline: none; border-bottom-color: #3730A3; }
`;

const SubmitButton = styled.button`
  font-family: 'Times New Roman', serif;
  background: #3730A3;
  color: white;
  border: none;
  padding: 0.85rem 1.8rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: fit-content;

  &:hover { background: #0b0b77; transform: translateY(-2px); box-shadow: 0 8px 25px rgba(55, 48, 163, 0.25); svg { transform: translateX(4px); } }
  svg { transition: transform 0.3s ease; }
  &:disabled { background: #cbd5e0; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const SuccessMessage = styled.div`
  font-family: 'Times New Roman', serif;
  background: #ecfdf5;
  color: #065f46;
  padding: 0.8rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
`;

const ErrorMessage = styled.div`
  font-family: 'Times New Roman', serif;
  color: #dc2626;
  font-size: 0.7rem;
  margin-top: 0.2rem;
`;

/* ========== BOTTOM BAR ========== */
const BottomBar = styled.div`
  max-width: 1000px;
  margin: 0 auto 2rem;
  padding: 0 5%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const BottomCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 1.2rem;
  text-align: center;
  box-shadow: 0 4px 15px rgba(55, 48, 163, 0.04);
  border: 1px solid #f0f0f5;

  .bottom-icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: ${props => props.bg || '#f5f3ff'};
    color: ${props => props.color || '#3730A3'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    margin: 0 auto 0.8rem;
  }

  h4 {
    font-family: 'Times New Roman', serif;
    color: #1a1a2e;
    font-size: 0.9rem;
    font-weight: 700;
    margin-bottom: 0.2rem;
  }

  p {
    font-family: 'Times New Roman', serif;
    color: #4a4a6a;
    font-size: 0.8rem;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', mobile: '', email: '', message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.replace(/\D/g, ''))) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setIsSubmitting(true);
    const whatsappMessage = `*New Callback Request*%0A%0A` +
      `*Name:* ${formData.firstName} ${formData.lastName}%0A` +
      `*Mobile:* ${formData.mobile}%0A` +
      `*Email:* ${formData.email}%0A` +
      `*Message:* ${formData.message}`;
    window.open(`https://wa.me/9000013099?text=${whatsappMessage}`, '_blank');
    setIsSuccess(true);
    setIsSubmitting(false);
    setTimeout(() => {
      setFormData({ firstName: '', lastName: '', mobile: '', email: '', message: '' });
      setIsSuccess(false);
    }, 3000);
  };

  return (
    <PageContainer>
      <TopSection>
        <TopTitle>We'd Love to <span>Hear From You</span></TopTitle>
        <TopSubtitle>Have questions? Our team is ready to help you start your medical coding journey.</TopSubtitle>
      </TopSection>

      <MainLayout>
        <ImagePanel>
          <ImageContent>
            <ImageTitle>Let's <span>Connect</span></ImageTitle>
            <ImageText>Our admission counselors are here to guide you through every step of your career journey.</ImageText>
            
            <ContactList>
              <ContactItem>
                <div className="icon-circle"><FaPhone /></div>
                <div className="info">+1 (888) 123-4567</div>
              </ContactItem>
              <ContactItem> 
                <div className="icon-circle"><FaEnvelope /></div>
                <div className="info">connect@learnthruus.com</div>
              </ContactItem> 
              <ContactItem>
                <div className="icon-circle"><FaMapMarkerAlt /></div>
                <div className="info">8-3-323, Unit No. 524, 4th Floor, Vasavi MPM Grand, Yellareddyguda,<br/>Ameerpet, Hyderabad, Telangana - 500 073</div>
              </ContactItem>
            </ContactList>
          </ImageContent>
          
          <ImageBottom>
            <img src="/images/contactus.png" alt="Contact Us" />
          </ImageBottom>
        </ImagePanel>

        <FormPanel>
          <FormTitle>Send Us a Message</FormTitle>
          <FormSubtitle>We'll get back to you within 24 hours</FormSubtitle>

          {isSuccess && (
            <SuccessMessage><FaCheckCircle /> Thank you! We'll contact you shortly.</SuccessMessage>
          )}

          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label>First Name <span>*</span></Label>
                <Input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" />
                {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
              </FormGroup>
              <FormGroup>
                <Label>Last Name <span>*</span></Label>
                <Input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" />
                {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Mobile Number <span>*</span></Label>
              <Input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} placeholder="9876543210" />
              {errors.mobile && <ErrorMessage>{errors.mobile}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>Email Address <span>*</span></Label>
              <Input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" />
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>Message <span>*</span></Label>
              <TextArea name="message" value={formData.message} onChange={handleChange} placeholder="Tell us how we can help you..." />
              {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'} <FaArrowRight />
            </SubmitButton>
          </Form>
        </FormPanel>
      </MainLayout>

      <BottomBar>
        <BottomCard bg="#f5f3ff" color="#3730A3">
          <div className="bottom-icon"><FaClock /></div>
          <h4>Response Time</h4>
          <p>Within 24 Hours</p>
        </BottomCard>
        <BottomCard bg="#f0fdf9" color="#0d9488">
          <div className="bottom-icon"><FaPhone /></div>
          <h4>Phone Support</h4>
          <p>Mon-Sat, 9AM-8PM</p>
        </BottomCard>
        <BottomCard bg="#fef9f0" color="#d97706">
          <div className="bottom-icon"><FaEnvelope /></div>
          <h4>Email Support</h4>
          <p>24/7 Available</p>
        </BottomCard>
      </BottomBar>
    </PageContainer>
  );
};

export default Contact;